import { useMemo } from "react";
import {
  APIOrchidDriveDirectory,
  APIOrchidDriveFile,
  DriveEntryErrorById,
  OrchidDriveData,
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "../types";

export type OrchidDrivePendingData = {
  files: { [name: string]: APIOrchidDriveFile };
  directories: { [name: string]: APIOrchidDriveDirectory };
};

export function anyPendingUpdated(
  fileUpdates: APIOrchidDriveFile[],
  directoryUpdates: APIOrchidDriveDirectory[],
  pending: OrchidDrivePendingData,
): boolean {
  return (
    fileUpdates.some((e) => {
      const existing = pending.files[e.file_data_id];
      return (
        !existing ||
        new Date(existing.modify_datetime).getTime() <
          new Date(e.modify_datetime).getTime()
      );
    }) ||
    directoryUpdates.some((e) => {
      const existing = pending.directories[e.directory_id];
      return (
        !existing ||
        new Date(existing.modify_datetime).getTime() <
          new Date(e.modify_datetime).getTime()
      );
    })
  );
}

export function anyUpdated(
  available: OrchidDriveData,
  fileUpdates: OrchidDriveFile[],
  directoryUpdates: OrchidDriveDirectory[],
): boolean {
  return (
    fileUpdates.some((e) => {
      const existing = available.files[e.fileDataId];
      return (
        !existing ||
        existing.modifyDatetime.getTime() < e.modifyDatetime.getTime()
      );
    }) ||
    directoryUpdates.some((e) => {
      const existing = available.directories[e.directoryId];
      return (
        !existing ||
        existing.modifyDatetime.getTime() < e.modifyDatetime.getTime()
      );
    })
  );
}

export function applyDriveDataUpdates(
  driveData: OrchidDriveData,
  fileUpdates: OrchidDriveFile[],
  directoryUpdates: OrchidDriveDirectory[],
): OrchidDriveData {
  if (!anyUpdated(driveData, fileUpdates, directoryUpdates)) return driveData;

  const newDriveData = {
    files: { ...driveData.files },
    directories: { ...driveData.directories },
  };
  for (const update of fileUpdates) {
    const existing = driveData.files[update.fileDataId];
    if (
      !existing ||
      existing.modifyDatetime.getTime() < update.modifyDatetime.getTime()
    ) {
      newDriveData.files[update.fileDataId] = update;
    }
  }
  for (const update of directoryUpdates) {
    const existing = driveData.directories[update.directoryId];
    if (
      !existing ||
      existing.modifyDatetime.getTime() < update.modifyDatetime.getTime()
    ) {
      newDriveData.directories[update.directoryId] = update;
    }
  }

  return newDriveData;
}

export function useMergedOrchidDriveFiles(
  driveData: OrchidDriveData,
  updates: OrchidDriveData,
): OrchidDriveData {
  return useMemo(
    () =>
      applyDriveDataUpdates(
        driveData,
        Object.values(updates.files),
        Object.values(updates.directories),
      ),
    [driveData, updates],
  );
}

export function useMergedDriveErrorByEntryIds(
  lhs: DriveEntryErrorById,
  rhs: DriveEntryErrorById,
) {
  return useMemo(() => {
    return { ...lhs, ...rhs };
  }, [lhs, rhs]);
}
